(() => {
  function initDiscount(root) {
    const apply = root.querySelector('[data-apply-discount]');
    if (!apply) return;
    const label = apply.querySelector('[data-discount-label]');
    const icon = apply.querySelector('[data-discount-icon]');
    const status = root.querySelector('[data-discount-status]');
    const fallbackHref = apply.getAttribute('href');
    const shopRoot = window.Shopify?.routes?.root || '/';
    let applying = false;
    let saved = false;
    let revision = 0;

    async function requestCart(path, options = {}) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(`${shopRoot}${path}`, {
          ...options, credentials: 'same-origin', cache: 'no-store', signal: controller.signal
        });
        if (!response.ok) throw new Error('Cart request failed');
        return await response.json();
      } finally {
        clearTimeout(timeout);
      }
    }

    function showDiscount(cart) {
      const code = cart.discount_codes?.find(discount => discount.code.toUpperCase() === 'AUTUMN20');
      saved = Boolean(code);
      label.textContent = code ? `AUTUMN20 ${code.applicable ? 'applied' : 'saved'}` : 'Apply AUTUMN20';
      icon.textContent = code ? '✓' : '→';
      apply.setAttribute('href', code ? '#thankful20-planners' : fallbackHref);
      status.textContent = !code ? 'US only · Use AUTUMN20 at checkout'
        : code.applicable ? '20% off eligible items in your cart. US only.'
        : cart.total_discount > 0 ? 'Code saved. Your cart is using another offer. US only.'
        : 'Saved for checkout. Add an eligible planner, notebook or sticker. US only.';
    }

    async function syncDiscount() {
      if (applying || !root.isConnected) return;
      const current = ++revision;
      try {
        const cart = await requestCart('cart.js');
        if (!applying && current === revision && root.isConnected) showDiscount(cart);
      } catch {
        // Keep the working Shopify discount link if the cart cannot be read.
      }
    }

    apply.addEventListener('click', async event => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (saved && !applying) return;
      event.preventDefault();
      if (applying) return;
      applying = true;
      revision++;
      apply.setAttribute('aria-busy', 'true');
      apply.setAttribute('aria-disabled', 'true');
      label.textContent = 'Applying AUTUMN20…';
      status.textContent = 'Saving your code…';
      try {
        const before = await requestCart('cart.js');
        const codes = new Map((before.discount_codes || []).map(discount => [discount.code.toUpperCase(), discount.code]));
        codes.set('AUTUMN20', 'AUTUMN20');
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true, detail: { sections }
        }));
        const bundledSections = [...new Set(sections)].slice(0, 5);
        const cart = await requestCart('cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: [...codes.values()].join(','), sections: bundledSections, sections_url: location.pathname })
        });
        if (bundledSections.length && bundledSections.every(section => typeof cart.sections?.[section] === 'string')) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true, detail: { baseEvent: 'discount:update', cart }
          }));
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
        if (!cart.discount_codes?.some(discount => discount.code.toUpperCase() === 'AUTUMN20')) throw new Error('Code not saved');
        showDiscount(cart);
      } catch {
        saved = false;
        label.textContent = 'Apply AUTUMN20';
        icon.textContent = '→';
        apply.setAttribute('href', fallbackHref);
        status.textContent = 'Couldn’t save your code. Try again or enter AUTUMN20 at checkout.';
      } finally {
        applying = false;
        apply.removeAttribute('aria-busy');
        apply.removeAttribute('aria-disabled');
      }
    });

    document.addEventListener('cart:change', event => {
      if (applying || !root.isConnected) return;
      if (Array.isArray(event.detail?.cart?.discount_codes)) {
        revision++;
        showDiscount(event.detail.cart);
      } else {
        syncDiscount();
      }
    });
    document.addEventListener('cart:refresh', syncDiscount);
    window.addEventListener('pageshow', event => { if (event.persisted) syncDiscount(); });
    syncDiscount();
  }

  function init(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    initDiscount(root);
    const cards = [...root.querySelectorAll('[data-card]')];
    const bundleCards = [...root.querySelectorAll('[data-bundle-card][data-bundle-kind="planner"]')];
    const coverGroups = [...root.querySelectorAll('[data-planner-cover-group]')];
    const preferredVariants = new Map(cards.map(card => [card, card.querySelector('[data-variant]:checked').value]));
    const buttons = [...root.querySelectorAll('[data-colour]')].filter(el => el.tagName === 'BUTTON');
    const cover = root.querySelector('select[data-cover]');
    const size = root.querySelector('select[data-size]');
    const videoTile = root.querySelector('[data-promo-video]');
    const video = videoTile?.querySelector('video');
    const videoCard = videoTile?.closest('[data-card]');
    const videoToggle = videoCard?.querySelector('[data-video-toggle]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let videoInView = false;
    let videoPreference = 'auto';
    function updateVideoToggle() {
      videoToggle.setAttribute('aria-label', video.paused ? 'Play A6 planner video' : 'Pause A6 planner video');
      videoToggle.querySelector('[data-video-pause]').hidden = video.paused;
      videoToggle.querySelector('[data-video-play]').hidden = !video.paused;
    }
    function updateVideo() {
      if (!video) return;
      const show = !videoCard.hidden && videoCard.querySelector('[data-variant]:checked').value === videoTile.dataset.videoVariant;
      videoTile.hidden = !show;
      videoToggle.hidden = !show;
      if (videoInView && show && (videoPreference === 'play' || (videoPreference === 'auto' && !reducedMotion.matches))) video.play().catch(() => {});
      else video.pause();
    }
    if (video) {
      video.addEventListener('play', updateVideoToggle);
      video.addEventListener('pause', updateVideoToggle);
      videoToggle.addEventListener('click', () => {
        videoPreference = video.paused ? 'play' : 'pause';
        updateVideo();
      });
      new IntersectionObserver(entries => {
        videoInView = entries[0].isIntersecting;
        updateVideo();
      }, { threshold: 0.25 }).observe(video);
      reducedMotion.addEventListener('change', updateVideo);
    }
    const isUnitedStates = root.dataset.country === 'US';
    const money = new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
      style: 'currency', currency: root.dataset.currency || 'CAD', currencyDisplay: 'code'
    });
    let colour = '';
    function updateCard(card) {
      const variant = card.querySelector('[data-variant]:checked');
      if (!variant) return;
      const url = new URL(card.dataset.productUrl, location.origin);
      url.searchParams.set('variant', variant.value);
      card.querySelectorAll('[data-product-link]').forEach(link => { link.href = url.href; });
      card.querySelectorAll('[data-product-link-wrap] a').forEach(link => { link.href = url.href; });
      const quickAddVariant = card.querySelector('[data-quick-add-variant]');
      if (quickAddVariant) quickAddVariant.value = variant.value;
      const purchase = card.querySelector('button[data-ho-action="add"], button[data-ho-action="preorder"]');
      if (purchase) {
        const available = variant.dataset.available === 'true';
        const preorder = available && variant.dataset.preorder === 'true';
        purchase.dataset.hoAction = preorder ? 'preorder' : 'add';
        purchase.disabled = !available;
        const label = purchase.querySelector('[data-ho-action-label]');
        if (label) label.textContent = !available ? purchase.dataset.hoSoldoutLabel : preorder ? purchase.dataset.hoPreorderLabel : purchase.dataset.hoAddLabel;
      }
      const img = card.querySelector('img');
      if (img && img.getAttribute('src') !== variant.dataset.image) {
        img.removeAttribute('srcset');
        img.src = variant.dataset.image;
        img.alt = variant.dataset.alt || variant.dataset.colour;
      }
      card.querySelector('[data-selected-colour]').textContent = variant.dataset.colour;
      card.querySelector('[data-t20-current-price]').textContent = money.format(Number(isUnitedStates ? variant.dataset.t20SalePrice : variant.dataset.t20UnitPrice) / 100);
      const regular = card.querySelector('[data-t20-regular-price]');
      if (regular) regular.textContent = money.format(Number(variant.dataset.t20UnitPrice) / 100);
      const list = card.querySelector('.thankful20__variant-swatches');
      const swatch = variant.closest('[data-variant-item]');
      if (swatch.offsetWidth) {
        if (swatch.offsetLeft < list.scrollLeft) list.scrollLeft = swatch.offsetLeft;
        else if (swatch.offsetLeft + swatch.offsetWidth > list.scrollLeft + list.clientWidth) list.scrollLeft = swatch.offsetLeft + swatch.offsetWidth - list.clientWidth;
      }
      if (card === videoCard) updateVideo();
    }
    function filter() {
      let count = 0;
      // Reveal groups before measuring swatch positions; hide empty groups below.
      coverGroups.forEach(group => { group.hidden = false; });
      cards.forEach(card => {
        const variants = [...card.querySelectorAll('[data-variant]')];
        const match = colour ? variants.find(variant => variant.dataset.colour === colour) : variants.find(variant => variant.value === preferredVariants.get(card)) || variants[0];
        card.hidden = !match || (cover.value && card.dataset.cover !== cover.value) || (size.value && card.dataset.size !== size.value);
        card.querySelector('.thankful20__variant-swatches').toggleAttribute('data-swatch-filtered', Boolean(colour));
        const showMore = card.querySelector('[data-show-more]');
        if (showMore) showMore.hidden = Boolean(colour);
        variants.forEach(variant => { variant.closest('[data-variant-item]').hidden = Boolean(colour && variant.dataset.colour !== colour); });
        if (!card.hidden) {
          match.checked = true;
          updateCard(card);
          count++;
        }
      });
      bundleCards.forEach(card => {
        card.hidden = Boolean((colour && !card.dataset.eligibleColours.split('|').includes(colour)) || (cover.value && card.dataset.cover !== cover.value) || (size.value && card.dataset.size !== size.value));
        if (!card.hidden) count++;
      });
      coverGroups.forEach(group => {
        const visible = [...group.querySelectorAll('[data-card], [data-bundle-card]')].filter(card => !card.hidden).length;
        group.hidden = visible === 0;
        group.querySelector('[data-cover-group-count]').textContent = `${visible} listing${visible === 1 ? '' : 's'}`;
      });
      updateVideo();
      root.querySelector('[data-count]').textContent = `${count} planner listings · available colours only`;
      root.querySelector('[data-empty]').hidden = count !== 0;
    }
    cards.forEach(card => {
      card.addEventListener('change', event => {
        if (!event.target.matches('[data-variant]') || !event.target.checked) return;
        if (!colour) preferredVariants.set(card, event.target.value);
        updateCard(card);
      });
    });
    buttons.forEach(button => button.addEventListener('click', () => {
      colour = button.dataset.colour;
      buttons.forEach(item => item.setAttribute('aria-pressed', String(item === button)));
      filter();
    }));
    cover.addEventListener('change', filter);
    size.addEventListener('change', filter);
    root.querySelector('[data-filters]').hidden = false;
    filter();
  }
  document.querySelectorAll('[data-thankful20]').forEach(init);
  document.addEventListener('shopify:section:load', event => {
    event.target.querySelectorAll('[data-thankful20]').forEach(init);
  });
})();
